import React, { useState, useEffect, useRef } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import {
    Button,
    Steps,
    Form,
    App,
} from 'antd';
// Import unified service
import { saveCatalogueUnified } from '../../services/api/unifiedCatalogueService';
import { mapFormToDatabase } from '../../services/utils/catalogueModificationUtils';
import {
    UploadOutlined as UploadFileIcon
} from '@ant-design/icons';
import ImagePreviewModal from './ImagePreviewModal';
import CatalogPreview from './CatalogPreview';
// Import step components
import CustomizationStep from './Steps/CustomizationStep';
// Services and utilities
import { convertColorToHex } from '../../services/utils/colorUtils';
import {
    isValidDomainFormat,
    isDomainClean,
    isDomainAvailable
} from '../../services/utils/validationUtils';
import {
    formatFullDomainUrl
} from '../../services/utils/domainUtils';
import BannerStep from './Steps/BannerStep';
import SEOStep from './Steps/SEOStep';
import ReviewStep from './Steps/ReviewStep';
import { createDefaultFormStructure } from '../../services/utils/catalogueDefaults';


const { Step } = Steps;

/**
 * A stepper component for creating a new catalogue
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onCancel - Function to handle cancel
 * @param {Function} props.onFinish - Function to handle finish
 * @param {number} props.currentStep - Current step index
 * @param {Function} props.setCurrentStep - Function to set current step
 */
const CreateCatalogStepper = ({
    onCancel,
    onFinish,
    currentStep,
    setCurrentStep,
    onFormDataChange,
    initialFormData = null,
    isEditMode = false,
    stepperKey,
    originalCatalogueId = null,
    currentDraftId = null
}) => {
    // Utility function to synchronize nested and flat SEO fields
    // Use centralized synchronization function from utils

    // --- HANDLER UNIFICATO ---
    const handleCreateCatalogue = async () => {
        try {
            // Use centralized mapping function to convert form data to database structure
            let finalData = mapFormToDatabase(formData);

            // If we are in edit mode and have a current draft ID, update the existing draft
            if (isEditMode && currentDraftId) {
                // Import updateDraftedCatalogue function
                const { updateDraftedCatalogue } = await import('../../services/api/draftedCatalogueService');

                await updateDraftedCatalogue(currentDraftId, finalData);

                // For draft updates, call onFinish with a special flag to indicate it's an update
                // This way the parent can handle it differently (close modal without showing success modal)
                if (onFinish) onFinish(finalData, currentDraftId, true); // true = isUpdate flag
            } else if (isEditMode && originalCatalogueId) {
                // If we are in edit mode and have an original catalogue ID,
                // we can use the unified service directly
                const options = {
                    formData: finalData,
                    isDraft: true, // Save as draft if in edit mode
                    originalCatalogueId
                };

                const catalogueId = await saveCatalogueUnified(options);

                if (onFinish) onFinish(finalData, catalogueId);
            } else {
                // For new catalogues, save directly using the unified service
                const options = {
                    formData: finalData,
                    isDraft: true // Always save as draft for new catalogues
                };

                const catalogueId = await saveCatalogueUnified(options);

                if (onFinish) onFinish(finalData, catalogueId);
            }
        } catch (error) {
            console.error('Error creating/updating catalogue:', error);
            messageApi.error(`Error: ${error.message || 'Failed to create/update catalogue'}`);
            // Don't call onFinish if there was an error
        }
    };

    // Get message instance from App context
    const { message: messageApi } = App.useApp();
    // Form state
    const [form] = Form.useForm();
    const defaultFormData = createDefaultFormStructure();
    const [formData, setFormData] = useState(initialFormData || defaultFormData);

    // Reset formData when initialFormData or isEditMode changes
    useEffect(() => {
        if (isEditMode && initialFormData) {
            // Create a copy for processing and reverse field mapping
            let processedFormData = cloneDeep(initialFormData);

            // --- Reverse Field Mapping: Convert database structure to form structure ---
            // Initialize nested objects if they don't exist
            if (!processedFormData.seo) {
                processedFormData.seo = {};
            }
            if (!processedFormData.permissions) {
                processedFormData.permissions = {};
            }

            // Use centralized mapping to convert form data to database structure
            // This ensures proper field mapping without conflicts

            // Basic fields mapping (database -> form)
            if (processedFormData.nameCatalog !== undefined) {
                processedFormData.catalogueName = processedFormData.nameCatalog;
            }
            if (processedFormData.webDomain !== undefined) {
                processedFormData.domain = processedFormData.webDomain;
            }

            // NON rimuoviamo i campi titleSeo e list_keywords perché sono necessari per il form
            // Manteniamo sia la struttura nidificata che i campi piatti
            // Solo rimuoviamo i campi delle descrizioni multilingua che sono gestiti tramite metaDescriptions
            const fieldsToRemove = [
                'descriptionIt', 'descriptionEn', 'descriptionFr', 'descriptionEs', 'descriptionDe'
            ];

            // Remove only multilingual description fields that are now in the nested structure
            fieldsToRemove.forEach(field => {
                if (processedFormData[field] !== undefined) {
                    delete processedFormData[field];
                }
            });

            // Manteniamo titleSeo, list_keywords, metaDescription e metaDescriptions

            // Customization fields reverse mapping (database -> form)
            if (processedFormData.catalogues_page_style) {
                const style = processedFormData.catalogues_page_style;
                processedFormData.customization = {
                    // Manteniamo i valori null se sono null nel database
                    primaryColor: style.primaryColor,
                    secondaryColor: style.secondaryColor,
                    backgroundColor: style.backgroundColor || '#FFFFFF',
                    actionButtonColor: style.actionButtonColor,
                    bannerBackgroundColor: style.bannerBackgroundColor,
                    // Border radius values
                    cardCornerTopLeft: style.cardCornerTopLeft ?? 0,
                    cardCornerTopRight: style.cardCornerTopRight ?? 0,
                    cardCornerBottomRight: style.cardCornerBottomRight ?? 0,
                    cardCornerBottomLeft: style.cardCornerBottomLeft ?? 0,
                    buttonCornerTopLeft: style.buttonCornerTopLeft ?? 0,
                    buttonCornerTopRight: style.buttonCornerTopRight ?? 0,
                    buttonCornerBottomRight: style.buttonCornerBottomRight ?? 0,
                    buttonCornerBottomLeft: style.buttonCornerBottomLeft ?? 0,
                    bannerCornerTopLeft: style.bannerCornerTopLeft ?? 0,
                    bannerCornerTopRight: style.bannerCornerTopRight ?? 0,
                    bannerCornerBottomRight: style.bannerCornerBottomRight ?? 0,
                    bannerCornerBottomLeft: style.bannerCornerBottomLeft ?? 0
                };

                // Banner fields reverse mapping
                if (!processedFormData.banner) {
                    processedFormData.banner = {};
                }
                processedFormData.banner = {
                    ...processedFormData.banner,
                    logo: style.catalogueLogo,
                    logoPosition: style.catalogueLogoPosition || 'center',
                    height: style.coverHeightDesktop || 100,
                    heightMobile: style.coverHeightMobile || 100,
                    heightOption: style.coverHeightDesktop ? style.coverHeightDesktop.toString() : '100',
                    heightMobileOption: style.coverHeightMobile ? style.coverHeightMobile.toString() : '100',
                    bannerImage: style.coverImg,
                    bannerImageMobile: style.coverImgMobile,
                    isLogoVisible: style.isLogoVisible ?? true,
                    isTitleVisible: style.isTitleVisible ?? true
                };
            }

            // Permissions fields reverse mapping (database -> form)
            if (processedFormData.isLoginRequired !== undefined) {
                processedFormData.permissions.requireLogin = processedFormData.isLoginRequired;
                delete processedFormData.isLoginRequired;
            }
            if (processedFormData.isIframeOnlyAccess !== undefined) {
                processedFormData.permissions.iframeOnlyAccess = processedFormData.isIframeOnlyAccess;
                delete processedFormData.isIframeOnlyAccess;
            }
            if (processedFormData.allowedDomain !== undefined) {
                processedFormData.permissions.allowedDomain = processedFormData.allowedDomain;
                delete processedFormData.allowedDomain;
            }
            if (processedFormData.loginPassword !== undefined) {
                processedFormData.permissions.loginPassword = processedFormData.loginPassword;
                delete processedFormData.loginPassword;
            }
            if (processedFormData.isPublic !== undefined) {
                processedFormData.permissions.public = processedFormData.isPublic;
                delete processedFormData.isPublic;
            }

            setFormData(processedFormData);
            form.setFieldsValue(processedFormData);
        } else {
            setFormData(defaultFormData);
            form.resetFields();
        }
    }, [stepperKey]);

    // Notify parent of formData changes for draft logic
    useEffect(() => {
        if (typeof onFormDataChange === 'function') {
            onFormDataChange(formData);
        }
    }, [formData, onFormDataChange]);

    // Keyword input state
    const [keywordInputVisible, setKeywordInputVisible] = useState(false);
    const [keywordInputValue, setKeywordInputValue] = useState('');

    // Image preview modal state
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    // Validation states
    const [domainValidating, setDomainValidating] = useState(false);
    const [, setFormattedDomainUrl] = useState('');

    // Color conversion is now imported from utils/colorUtils.js

    // Handle form field changes
    const handleFormChange = (changedValues, allValues) => {
        isFormUpdating.current = true; // Prevent the useEffect from updating the form
        setFormData(prevFormData => {
            let newFormData = { ...prevFormData };

            // Check if we have color changes in customization
            if (changedValues.customization) {
                const { customization } = changedValues;
                // Process color fields
                if (customization.primaryColor) {
                    newFormData.customization.primaryColor = convertColorToHex(customization.primaryColor);
                }
                if (customization.secondaryColor) {
                    newFormData.customization.secondaryColor = convertColorToHex(customization.secondaryColor);
                }
                if (customization.backgroundColor) {
                    newFormData.customization.backgroundColor = convertColorToHex(customization.backgroundColor);
                }
                if (customization.actionButtonColor) {
                    newFormData.customization.actionButtonColor = convertColorToHex(customization.actionButtonColor);
                }
                if (customization.bannerBackgroundColor) {
                    newFormData.customization.bannerBackgroundColor = convertColorToHex(customization.bannerBackgroundColor);
                }

                // Handle border radius values from BorderRadiusControl
                // Card corners
                if (customization.cardCornerTopLeft !== undefined) newFormData.customization.cardCornerTopLeft = customization.cardCornerTopLeft;
                if (customization.cardCornerTopRight !== undefined) newFormData.customization.cardCornerTopRight = customization.cardCornerTopRight;
                if (customization.cardCornerBottomRight !== undefined) newFormData.customization.cardCornerBottomRight = customization.cardCornerBottomRight;
                if (customization.cardCornerBottomLeft !== undefined) newFormData.customization.cardCornerBottomLeft = customization.cardCornerBottomLeft;

                // Button corners
                if (customization.buttonCornerTopLeft !== undefined) newFormData.customization.buttonCornerTopLeft = customization.buttonCornerTopLeft;
                if (customization.buttonCornerTopRight !== undefined) newFormData.customization.buttonCornerTopRight = customization.buttonCornerTopRight;
                if (customization.buttonCornerBottomRight !== undefined) newFormData.customization.buttonCornerBottomRight = customization.buttonCornerBottomRight;
                if (customization.buttonCornerBottomLeft !== undefined) newFormData.customization.buttonCornerBottomLeft = customization.buttonCornerBottomLeft;

                // Banner corners
                if (customization.bannerCornerTopLeft !== undefined) newFormData.customization.bannerCornerTopLeft = customization.bannerCornerTopLeft;
                if (customization.bannerCornerTopRight !== undefined) newFormData.customization.bannerCornerTopRight = customization.bannerCornerTopRight;
                if (customization.bannerCornerBottomRight !== undefined) newFormData.customization.bannerCornerBottomRight = customization.bannerCornerBottomRight;
                if (customization.bannerCornerBottomLeft !== undefined) newFormData.customization.bannerCornerBottomLeft = customization.bannerCornerBottomLeft;

            }

            // Special handling for SEO fields
            if (changedValues.seo) {
                // First, merge the SEO changes into the form data
                if (!newFormData.seo) {
                    newFormData.seo = {};
                }

                // Deep merge SEO changes - handle metaDescriptions specially
                if (changedValues.seo.metaDescriptions) {
                    // Initialize metaDescriptions if it doesn't exist
                    if (!newFormData.seo.metaDescriptions) {
                        newFormData.seo.metaDescriptions = {};
                    }

                    // Merge only the changed languages, preserving existing ones
                    newFormData.seo.metaDescriptions = {
                        ...newFormData.seo.metaDescriptions,
                        ...changedValues.seo.metaDescriptions
                    };
                }

                // Merge other SEO fields (excluding metaDescriptions which was handled above)
                const { metaDescriptions, ...otherSeoChanges } = changedValues.seo;
                newFormData.seo = {
                    ...newFormData.seo,
                    ...otherSeoChanges
                };

                // SEO fields are now handled by centralized mapping at save time

            }

            // Process all other changes (merge nested objects to avoid losing flags)
            for (const key in changedValues) {
                if (changedValues.hasOwnProperty(key) && key !== 'customization' && key !== 'seo') {
                    const value = changedValues[key];
                    if (Array.isArray(key)) {
                        let currentLevel = newFormData;
                        key.forEach((k, index) => {
                            if (index === key.length - 1) {
                                currentLevel[k] = value;
                                if (key[0] === 'banner' && (key[1] === 'heightOption' || key[1] === 'heightMobileOption')) {
                                    if (value !== 'custom') {
                                        const heightField = key[1] === 'heightOption' ? 'height' : 'heightMobile';
                                        currentLevel[heightField] = parseInt(value, 10);
                                    }
                                }
                            } else {
                                if (!currentLevel[k]) {
                                    currentLevel[k] = {};
                                }
                                currentLevel = currentLevel[k];
                            }
                        });
                    } else if (typeof value === 'object' && !Array.isArray(value) && newFormData[key] && typeof newFormData[key] === 'object') {
                        // Merge nested object changes (e.g., banner, etc.)
                        newFormData[key] = { ...newFormData[key], ...value };
                    } else {
                        newFormData[key] = value;
                    }
                }
            }

            return newFormData;
        });
    };

    // Sync form values when formData changes - moved outside of setState to avoid render-time updates
    // Use a ref to track if the update comes from form itself to avoid loops
    const isFormUpdating = useRef(false);

    useEffect(() => {
        if (form && formData && !isFormUpdating.current) {
            isFormUpdating.current = true;
            form.setFieldsValue(formData);
            // Reset the flag after a short delay to allow form updates
            setTimeout(() => {
                isFormUpdating.current = false;
            }, 0);
        }
    }, [form, formData]);

    // Handle image preview
    const handlePreview = (file) => {
        try {
            // Handle null or undefined
            if (!file) {
                console.warn('Preview attempted on null or undefined file');
                return;
            }

            // Handle different file types safely
            let previewUrl = '';
            let fileName = 'Image Preview';

            // If it's a string (direct URL)
            if (typeof file === 'string') {
                previewUrl = file;
            }
            // If it's a File object
            else if (file instanceof File) {
                previewUrl = URL.createObjectURL(file);
                fileName = file.name || fileName;
            }
            // If it's an Ant Design Upload file object with originFileObj
            else if (file.originFileObj instanceof File) {
                previewUrl = URL.createObjectURL(file.originFileObj);
                fileName = file.name || file.originFileObj.name || fileName;
            }
            // If it has a url property that's a string
            else if (typeof file.url === 'string') {
                previewUrl = file.url;
                fileName = file.name || fileName;
            }
            // If it has a thumbUrl property that's a string
            else if (typeof file.thumbUrl === 'string') {
                previewUrl = file.thumbUrl;
                fileName = file.name || fileName;
            }
            // Fallback - try to get any string representation
            else if (file.toString && typeof file.toString() === 'string' && file.toString() !== '[object Object]') {
                previewUrl = file.toString();
            }

            // Set preview data only if we have a valid URL
            if (previewUrl) {
                setPreviewImage(previewUrl);
                setPreviewTitle(fileName);
                setPreviewVisible(true);
            } else {
                console.warn('Could not determine preview URL for file:', file);
            }
        } catch (error) {
            console.error('Error in handlePreview:', error);
        }
    };

    // Handle keyword input
    const handleKeywordInputChange = (e) => {
        setKeywordInputValue(e.target.value);
    };

    const handleKeywordInputConfirm = () => {
        const keywordsLimit = 6; // Match the limit defined in SEOStep
        if (keywordInputValue &&
            formData.seo?.keywords?.length < keywordsLimit &&
            !formData.seo.keywords.includes(keywordInputValue)) {
            // Update both nested and flat structure
            const updatedKeywords = [...(formData.seo?.keywords || []), keywordInputValue];
            setFormData(prev => ({
                ...prev,
                seo: {
                    ...prev.seo,
                    keywords: updatedKeywords
                },
                // Also update the flat field for direct database mapping
                list_keywords: updatedKeywords
            }));
        }
        setKeywordInputVisible(false);
        setKeywordInputValue('');
    };

    const handleRemoveKeyword = (removedTag) => {
        if (!formData.seo?.keywords) return;

        const keywords = formData.seo.keywords.filter(tag => tag !== removedTag);
        // Update both nested and flat structure
        setFormData(prev => ({
            ...prev,
            seo: {
                ...prev.seo,
                keywords
            },
            // Also update the flat field for direct database mapping
            list_keywords: keywords
        }));
    };

    // Steps content
    const steps = [
        {
            title: 'Customization',
            content: (
                <CustomizationStep
                    form={form}
                    formData={formData}
                    handleFormChange={handleFormChange}
                    isDomainClean={isDomainClean}
                    isValidDomainFormat={isValidDomainFormat}
                    isDomainAvailable={isDomainAvailable}
                    formatDomainUrl={formatFullDomainUrl}
                    setDomainValidating={setDomainValidating}
                    domainValidating={domainValidating}
                    setFormattedDomainUrl={setFormattedDomainUrl}
                />
            )
        },
        {
            title: 'Banner',
            content: (
                <BannerStep
                    form={form}
                    formData={formData}
                    handlePreview={handlePreview}
                    handleFormChange={handleFormChange}
                    UploadFileIcon={UploadFileIcon}
                />
            )
        },
        {
            title: 'SEO',
            content: (
                <SEOStep
                    form={form}
                    formData={formData}
                    handleFormChange={handleFormChange}
                    handleKeywordInputChange={handleKeywordInputChange}
                    handleKeywordInputConfirm={handleKeywordInputConfirm}
                    handlePreview={handlePreview}
                    handleRemoveKeyword={handleRemoveKeyword}
                    keywordInputVisible={keywordInputVisible}
                    setKeywordInputVisible={setKeywordInputVisible}
                    keywordInputValue={keywordInputValue}
                />
            )
        },
        {
            title: 'Review',
            content: (
                <ReviewStep
                    formData={formData}
                />
            )
        }
    ];

    // Function to handle next step
    const handleNext = () => {
        form.validateFields()
            .then(() => {
                setCurrentStep(currentStep + 1);
                setViewMode('edit'); // Set view to 'edit' on successful next step
            })
            .catch(error => {
                console.error('Validation failed:', error);
                messageApi.error('Please fix the validation errors before proceeding');
            });
    };

    // Function to handle previous step
    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
        setViewMode('edit');
    };

    // Determine if the Next button should be disabled
    const isNextDisabled = () => {
        if (currentStep === 0) {
            // Customization step - check for required fields
            // Only check catalogueName since domain field is not present in CreateCatalogStepper
            return !formData.catalogueName;
        } else if (currentStep === 1) {
            // Banner step - check for logo
            return !formData.banner.logo;
        }
        return false;
    };

    // Stato per controllare la visualizzazione dell'anteprima
    const [viewMode, setViewMode] = useState('edit'); // 'edit' o 'preview'

    return (
        <div style={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <ImagePreviewModal
                visible={previewVisible}
                onClose={() => setPreviewVisible(false)}
                imageUrl={previewImage}
                title={previewTitle}
            />
            <div style={{ display: 'flex', gap: '1.5rem', height: '35rem', width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                {/* Sezione sinistra con steps */}
                <div style={{ width: '15.625rem', flexShrink: 0, borderRight: '1px solid #f0f0f0', paddingRight: '1rem' }}>
                    <Steps current={currentStep} direction="vertical" style={{ height: '100%' }}>
                        {steps.map(item => (
                            <Step key={item.title} title={item.title} />
                        ))}
                    </Steps>
                </div>

                {/* Contenuto principale con titolo e bottoni */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: 'calc(100% - 17rem)', boxSizing: 'border-box', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', paddingLeft: '0.5rem' }}>
                        {viewMode === 'edit' ? (
                            <Button type="primary" onClick={() => setViewMode('preview')}>Preview</Button>
                        ) : (
                            <Button type="primary" onClick={() => setViewMode('edit')}>Go Back</Button>
                        )}
                    </div>

                    {/* Contenuto in base alla modalità */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', width: '100%', boxSizing: 'border-box' }}>
                        {viewMode === 'edit' ? (
                            // Modalità Edit - Mostra il contenuto dello step corrente
                            <div className="steps-content" style={{ width: '100%', boxSizing: 'border-box' }}>
                                {steps[currentStep].content}
                            </div>
                        ) : (
                            // Modalità Preview - Mostra l'anteprima del catalogo
                            <CatalogPreview formData={formData} />
                        )}
                    </div>
                </div>
            </div>
            <div className="steps-action" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
                {currentStep > 0 && (
                    <Button onClick={handlePrev}>
                        Previous
                    </Button>
                )}

                {currentStep === 0 && (
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                )}

                {currentStep < steps.length - 1 && (
                    <Button type="primary" onClick={handleNext} disabled={isNextDisabled()}>
                        Next
                    </Button>
                )}

                {currentStep === steps.length - 1 && (
                    <Button type="primary" onClick={handleCreateCatalogue}>
                        {isEditMode ? 'Update Catalogue' : 'Create Catalogue'}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default CreateCatalogStepper;